// Localização: frontend/src/components/Prontuario/Profissional/RH/FaltasSection.jsx (versão 1.0.0)
// Seção de Faltas do prontuário do profissional

import React, { useState, useEffect } from 'react';
import { Plus, CalendarX, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import { getFaltas, createFalta } from '../../../../api/api';
import { formatDate } from '../../../../utils/formatters';
import FaltaFormModal from './FaltaFormModal';

const FaltasSection = ({ profissionalId, onRefresh }) => {
    const [faltas, setFaltas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isFormOpen, setIsFormOpen] = useState(false);

    useEffect(() => {
        fetchFaltas();
    }, [profissionalId]);

    const fetchFaltas = async () => {
        try {
            setLoading(true);
            const response = await getFaltas(profissionalId);
            if (response.data.success) {
                setFaltas(response.data.data || []);
            }
        } catch (error) {
            console.error('Erro ao buscar faltas:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateSuccess = () => {
        setIsFormOpen(false);
        fetchFaltas();
        if (onRefresh) onRefresh();
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'aprovada':
                return <CheckCircle className="h-5 w-5 text-green-500" />;
            case 'rejeitada':
                return <XCircle className="h-5 w-5 text-red-500" />;
            default:
                return <AlertCircle className="h-5 w-5 text-yellow-500" />;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'aprovada':
                return 'bg-green-50 border-green-200';
            case 'rejeitada':
                return 'bg-red-50 border-red-200';
            default:
                return 'bg-yellow-50 border-yellow-200';
        }
    };

    const getTipoFaltaLabel = (tipo) => {
        const labels = {
            'justificada': 'Justificada',
            'nao_justificada': 'Não Justificada',
            'atraso': 'Atraso',
            'saida_antecipada': 'Saída Antecipada'
        };
        return labels[tipo] || tipo;
    };

    if (loading) {
        return (
            <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Histórico de Faltas</h3>
                <button
                    onClick={() => setIsFormOpen(true)}
                    className="flex items-center space-x-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                >
                    <Plus className="h-4 w-4" />
                    <span>Registrar Falta</span>
                </button>
            </div>

            {faltas.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                    <CalendarX className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                    <p>Nenhuma falta registrada.</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {faltas.map((falta) => (
                        <div
                            key={falta.id}
                            className={`border rounded-lg p-4 ${getStatusColor(falta.status)}`}
                        >
                            <div className="flex justify-between items-start">
                                <div className="flex-1">
                                    <div className="flex items-center space-x-2 mb-2">
                                        {getStatusIcon(falta.status)}
                                        <span className="font-semibold text-gray-900">
                                            {formatDate(falta.data_falta)}
                                        </span>
                                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                                            falta.status === 'aprovada' ? 'bg-green-100 text-green-800' :
                                            falta.status === 'rejeitada' ? 'bg-red-100 text-red-800' :
                                            'bg-yellow-100 text-yellow-800'
                                        }`}>
                                            {falta.status}
                                        </span>
                                    </div>
                                    <div className="text-sm text-gray-600 space-y-1">
                                        <div>
                                            <span className="font-medium">Tipo:</span> {getTipoFaltaLabel(falta.tipo_falta)}
                                        </div>
                                        {falta.motivo && (
                                            <div>
                                                <span className="font-medium">Motivo:</span> {falta.motivo}
                                            </div>
                                        )}
                                        {falta.justificativa && (
                                            <div>
                                                <span className="font-medium">Justificativa:</span> {falta.justificativa}
                                            </div>
                                        )}
                                        {falta.horas_faltadas && (
                                            <div>
                                                <span className="font-medium">Horas Faltadas:</span> {falta.horas_faltadas}h
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <FaltaFormModal
                isOpen={isFormOpen}
                onClose={() => setIsFormOpen(false)}
                profissionalId={profissionalId}
                onSuccess={handleCreateSuccess}
            />
        </div>
    );
};

export default FaltasSection;

